import type { DashboardTaskWithEntityReferences } from "@dune/types";
import { PaginatedDataTable, useSidebar } from "@dune/ui";
import { useNavigate } from "react-router-dom";
import { useGetAllActiveTasks } from "../../api/queries/tasks.queries";
import ErrorBoundary from "../../component/common/ErrorBoundary";
import PageLayoutWithTabs from "../../component/layout/PageLayoutWithTabs";
import { usePagination } from "../../hooks/usePagination";
import { useTaskActions } from "../../stores/task.store";
import { dashboardTasksColumns } from "./dashboardTasksColumns";

export const ListTasksPage = () => {
	const navigate = useNavigate();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const pagination = usePagination();
	const {
		data: tasksData,
		isLoading,
		error,
	} = useGetAllActiveTasks({
		page: pagination.page,
		limit: pagination.limit,
	});
	const { selectTask } = useTaskActions();

	const tasks = tasksData?.data ?? [];
	const meta = tasksData?.meta ?? {
		page: pagination.page,
		limit: pagination.limit,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const handleTaskClick = (task: DashboardTaskWithEntityReferences) => {
		if (!task.relatedEntities || task.relatedEntities.length === 0) {
			return;
		}

		const entity = task.relatedEntities[0];
		selectTask(task.id.toString());

		let redirectPath = "";

		switch (entity.entityType) {
			case "BROKER":
				redirectPath = `/brokers/${entity.entityId}?tab=taches`;
				break;
			case "CONSTRUCTION_SITE":
				redirectPath = `/projects/${entity.entityId}?tab=taches`;
				break;
			case "PROPOSITION":
				redirectPath = `/proposals/${entity.entityId}?tab=taches`;
				break;
			default:
				return;
		}

		navigate(redirectPath);
	};

	if (error) {
		return (
			<PageLayoutWithTabs title="Tâches" tabContent={{}}>
				<div className="text-center py-8">
					<h1 className="text-2xl font-semibold text-red-600 mb-2">
						Erreur lors du chargement des tâches
					</h1>
					<p className="text-gray-600">
						Impossible de charger les tâches. Veuillez réessayer plus tard.
					</p>
				</div>
			</PageLayoutWithTabs>
		);
	}

	return (
		<ErrorBoundary fallbackMessage="Erreur lors du chargement des tâches.">
			<PageLayoutWithTabs title="Tâches" tabContent={{}}>
				<div className="space-y-6 flex-1 min-h-0 flex flex-col">
					<div className="flex-1 min-h-0 w-full overflow-auto">
						<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
							<PaginatedDataTable
								data={tasks}
								columns={dashboardTasksColumns}
								noResultsText="Aucune tâche trouvée"
								isLoading={isLoading}
								onRowClick={handleTaskClick}
								variant="static-clean"
								meta={meta}
								onPageChange={pagination.handlePageChange}
								onLimitChange={pagination.handleLimitChange}
							/>
						</div>
					</div>
				</div>
			</PageLayoutWithTabs>
		</ErrorBoundary>
	);
};
